import React, { useState, useEffect } from "react";
import { SovosTab, SovosTabs, SovosToolbarDropdown, SovosMenuItem } from "mosaic-react";
import { useGlobalValue } from "colbi_web_ui/lib/state/GlobalProvider";
import {
    filteredNavigationByModulesForNestedLinks,
    tabsRepositoryNavigationByModules,
} from "../../controllers/Repository/navigation";
import { hydrateRoute } from "colbi_web_ui/lib/utils";
import { regexURLMatcher } from "colbi_web_ui/lib/utils/routingUtils";
import SovosMonthSelector from "colbi_web_ui/lib/components/layout/YearSelector";
import styles from "./RepositoryTabs.module.sass";
import { hasModule } from "../../utils/utils";

const buildTabs = (path, lastPart) => {
    const splitArray = path.split("/");
    splitArray[splitArray.length - 1] = lastPart;
    return splitArray.join("/");
};

const RepositoryTabs = ({
    viewMode: viewModeProp,
    onViewModeChange,
    fiscalYear: fiscalYearProp,
    fiscalYears: fiscalYearsProp,
    history: historyProp,
    location: locationProp,
    hasSource,
    hasLegal,
}) => {
    const { history, location, i18n, modules, params } = useGlobalValue();
    const [selectedTab, setSelectedTab] = useState(
        buildTabs(location.pathname, location.pathname.split("/")?.pop()),
    );
    const [currentFiscalYears, setCurrentFiscalYears] = useState([]);
    const [arrayImportIds, setArrayImportIds] = useState([]);
    const [currentFile, setCurrentFile] = useState(params.importId || "full");

    const viewMode = viewModeProp || "source";
    const currentFiscalYear = fiscalYearProp || params.fiscalYear;

    // Feature flag: Only show Source/Legal toggle for projects with "new_navigation" module
    const hasNewAudit = hasModule(modules, "new_navigation");

    // Process fiscal years when props change
    useEffect(() => {
        if (!fiscalYearsProp) return;

        let yearsArray;
        if (Array.isArray(fiscalYearsProp)) {
            // If it's already an array, just convert strings to integers
            yearsArray = fiscalYearsProp.map((year) => parseInt(year));
        } else if (typeof fiscalYearsProp === "object") {
            // If it's an object, get the keys and convert to integers
            yearsArray = Object.keys(fiscalYearsProp).map((year) => parseInt(year));
        } else {
            return;
        }

        setCurrentFiscalYears(yearsArray);
    }, [fiscalYearsProp]);

    // Process import IDs for current fiscal year
    useEffect(() => {
        if (
            fiscalYearsProp &&
            currentFiscalYear &&
            typeof fiscalYearsProp === "object" &&
            !Array.isArray(fiscalYearsProp)
        ) {
            const importIdsForYear = fiscalYearsProp[currentFiscalYear]?.[viewMode];
            if (Array.isArray(importIdsForYear)) {
                setArrayImportIds(importIdsForYear);
            }
        }
    }, [fiscalYearsProp, currentFiscalYear, viewMode]);

    // Update currentFile when URL changes
    useEffect(() => {
        if (params.importId) {
            setCurrentFile(params.importId);
        }
    }, [params.importId]);

    const handleViewModeChange = (newValue) => {
        if (onViewModeChange && typeof onViewModeChange === "function") {
            onViewModeChange(newValue);
        }
    };

    const handleFiscalYearChange = (e) => {
        const newYear = e.year;
        const currentData = fiscalYearsProp[newYear];
        let lastImportId = params.importId || "full";

        if (Array.isArray(currentData)) {
            let fiscalYearIds = currentData.map((item) => item.id);
            lastImportId =
                fiscalYearIds.indexOf("full") !== -1
                    ? "full"
                    : Math.max(...fiscalYearIds.filter((id) => typeof id === "number"));
        }

        const myRegex = /^(\/(pt|en|lt|es|ro|fr)\/\d+\/)(\d{4}|current)\/?(\d+|full)?(\/*.*)/;
        const match = regexURLMatcher(myRegex, history.location.pathname);

        if (match && match[4]) {
            const newPath = history.location.pathname.replace(
                myRegex,
                `$1${newYear}/${lastImportId}$5`,
            );
            const searchParams = new URLSearchParams(location.search);
            const newUrl = searchParams.toString()
                ? `${newPath}?${searchParams.toString()}`
                : newPath;
            history.push(newUrl);
        } else {
            const newPath = history.location.pathname.replace(myRegex, `$1${newYear}/$5`);
            const searchParams = new URLSearchParams(location.search);
            const newUrl = searchParams.toString()
                ? `${newPath}?${searchParams.toString()}`
                : newPath;
            history.push(newUrl);
        }
    };

    const repositoryNavigation = filteredNavigationByModulesForNestedLinks(
        modules,
        tabsRepositoryNavigationByModules,
    );

    const tabs = repositoryNavigation.map((elem, index) => {
        const { name, icon: Icon, to } = elem;
        return (
            <SovosTab
                key={index}
                value={hydrateRoute(to, params)}
                icon={<Icon />}
                label={i18n`${name}`}
            />
        );
    });

    return (
        <div>
            {/* Top row: Source/Legal toggle, Fiscal Year selector, and Import ID dropdown */}
            <div className={styles["top-controls"]} role="toolbar" aria-label="View controls">
                {/* Source/Legal View Mode Toggle - Only for projects with new_navigation module */}
                {hasNewAudit && (
                    <div
                        className={styles["view-toggle"]}
                        role="tablist"
                        aria-label="View mode selector"
                    >
                        <SovosTabs
                            value={viewMode}
                            onChange={(_, newValue) => handleViewModeChange(newValue)}
                        >
                            <SovosTab value="source" label="Source" disabled={!hasSource} />
                            <SovosTab value="legal" label="Legal" disabled={!hasLegal} />
                        </SovosTabs>
                    </div>
                )}

                {/* Fiscal Year Selector */}
                {currentFiscalYears.length > 0 && (
                    <SovosMonthSelector
                        className={styles["sovos-month-selector"]}
                        format={"number"}
                        selectedYear={parseInt(currentFiscalYear)}
                        nextMonthTooltipText={"Next"}
                        previousMonthTooltipText={"Previous"}
                        years={currentFiscalYears}
                        minDate={new Date(`${currentFiscalYears[0]}-01-01`)}
                        maxDate={
                            new Date(`${currentFiscalYears[currentFiscalYears.length - 1]}-12-31`)
                        }
                        onChange={handleFiscalYearChange}
                    />
                )}

                {/* Import ID Dropdown (full or specific import) */}
                {arrayImportIds.length > 0 && (
                    <div className={styles["import-dropdown-wrapper"]}>
                        <SovosToolbarDropdown
                            className={styles["toolbar-dropdown"]}
                            value={currentFile}
                            onChange={(event) => {
                                const newImportId = event.target.value;
                                setCurrentFile(newImportId);
                                const myRegex =
                                    /^(\/(pt|en|lt|es|ro|fr)\/\d+\/)(\d{4}|current)\/?(\d+|full)?(\/*.*)/;

                                const newPathname = history.location.pathname.replace(
                                    myRegex,
                                    `$1$3/${newImportId}$5`,
                                );

                                // Preserve query parameters (like viewMode)
                                const searchParams = new URLSearchParams(location.search);
                                const newUrl = searchParams.toString()
                                    ? `${newPathname}?${searchParams.toString()}`
                                    : newPathname;

                                history.push(newUrl);
                            }}
                        >
                            {arrayImportIds.map((importItem) => (
                                <SovosMenuItem key={importItem.id} value={importItem.id}>
                                    {importItem.id === "full"
                                        ? importItem.name
                                        : `${importItem.id} - ${importItem.name}`}
                                </SovosMenuItem>
                            ))}
                        </SovosToolbarDropdown>
                    </div>
                )}
            </div>

            {/* Repository Navigation Tabs (Data Upload, Imported Files, etc.) */}
            <SovosTabs
                onChange={(_, value) => {
                    setSelectedTab(value);
                    // Preserve query parameters (especially viewMode) when navigating
                    const searchParams = new URLSearchParams(location.search);
                    const newUrl = searchParams.toString()
                        ? `${value}?${searchParams.toString()}`
                        : value;
                    history.push(newUrl);
                }}
                value={selectedTab}
            >
                {tabs}
            </SovosTabs>
        </div>
    );
};

export default RepositoryTabs;
